import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";

import { updateOrg, type Org, type OrgUpdate } from "@/libs/onboardingApi";
import { isTaxIdReady, TAX_ID_HELPER_TEXT } from "../taxId";
import { FormMessage } from "./shared";

const LOCKED_ORG_STATUSES = new Set<string>([
  "approved",
  "active",
  "suspended",
]);

export function BusinessProfilePanel({
  org,
  orgId,
}: {
  org: Org;
  orgId: string;
}) {
  const queryClient = useQueryClient();
  const [businessName, setBusinessName] = useState(org.business_name ?? "");
  const [legalName, setLegalName] = useState(org.legal_name ?? "");
  const [taxId, setTaxId] = useState(org.tax_id ?? "");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const identityLocked = LOCKED_ORG_STATUSES.has(org.status);
  const taxIdReady = isTaxIdReady(taxId);

  useEffect(() => {
    setBusinessName(org.business_name ?? "");
    setLegalName(org.legal_name ?? "");
    setTaxId(org.tax_id ?? "");
  }, [org.business_name, org.legal_name, org.tax_id]);

  const mutation = useMutation({
    mutationFn: (payload: OrgUpdate) => updateOrg(orgId, payload),
    onSuccess: () => {
      setMessage("Saved.");
      setError("");
      void queryClient.invalidateQueries({ queryKey: ["org", "me"] });
      void queryClient.invalidateQueries({
        queryKey: ["org", orgId, "onboarding-state"],
      });
    },
    onError: (err: Error) => {
      setMessage("");
      setError(err.message || "Could not save business profile.");
    },
  });

  function buildProfilePayload(): OrgUpdate | null {
    if (identityLocked) return null;
    const payload: OrgUpdate = {};
    if (businessName.trim() !== (org.business_name ?? "").trim()) {
      payload.business_name = businessName.trim() || null;
    }
    if (legalName.trim() !== (org.legal_name ?? "").trim()) {
      payload.legal_name = legalName.trim() || null;
    }
    if (taxId.trim() !== (org.tax_id ?? "").trim()) {
      payload.tax_id = taxId.trim() || null;
    }
    return Object.keys(payload).length > 0 ? payload : null;
  }

  function autosaveProfile() {
    const payload = buildProfilePayload();
    if (!payload || mutation.isPending) return;
    setMessage("");
    setError("");
    if (
      (payload.business_name !== undefined && !businessName.trim()) ||
      (payload.legal_name !== undefined && !legalName.trim())
    ) {
      setError("Business name and legal name are required.");
      return;
    }
    if (payload.tax_id !== undefined && !taxIdReady) {
      setError("Enter a 9-digit EIN or tax ID before saving.");
      return;
    }
    mutation.mutate(payload);
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    autosaveProfile();
  }

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={2}>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <TextField
          disabled={identityLocked || mutation.isPending}
          label="Business name"
          onBlur={autosaveProfile}
          onChange={(event) => setBusinessName(event.target.value)}
          placeholder="Downtown Badminton LLC"
          value={businessName}
        />
        <TextField
          disabled={identityLocked || mutation.isPending}
          label="Legal name"
          onBlur={autosaveProfile}
          onChange={(event) => setLegalName(event.target.value)}
          placeholder="Downtown Badminton LLC"
          value={legalName}
        />
        <TextField
          disabled={identityLocked || mutation.isPending}
          helperText={TAX_ID_HELPER_TEXT}
          label="Tax number"
          onBlur={autosaveProfile}
          onChange={(event) => setTaxId(event.target.value)}
          placeholder="12-3456789"
          slotProps={{ htmlInput: { "aria-label": "Tax ID / EIN" } }}
          value={taxId}
        />
      </Box>
      {identityLocked ? (
        <FormMessage tone="error">
          Business identity is locked after platform approval.
        </FormMessage>
      ) : null}
      {mutation.isPending ? (
        <Typography color="text.secondary" role="status" variant="caption">
          Saving…
        </Typography>
      ) : null}
      {message ? <FormMessage tone="success">{message}</FormMessage> : null}
      {error ? <FormMessage tone="error">{error}</FormMessage> : null}
    </Stack>
  );
}
